import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import {
  FileText,
  Save,
  Target,
  Users,
  Sparkles,
  GraduationCap,
  Tag,
  BookOpen,
  Settings2,
  ChevronRight,
  Loader2,
  Trash2,
  RefreshCw,
  ExternalLink,
  Star,
  Search,
  Plus,
  X,
  Check,
  AlertCircle,
} from "lucide-react";
import {
  getMetadataOptions,
  createResource,
  updateResource,
  getResourceVariants,
  generateResourceVariants,
  deleteResourceVariant,
  regenerateVariant,
  listStudents,
  searchSkills,
  getSkillsRepositories,
} from "../../lib/api";
import { toast } from "sonner";

const RESOURCE_TYPES = [
  { value: "fiche", label: "Fiche" },
  { value: "exercice", label: "Exercice" },
  { value: "evaluation", label: "Evaluation" },
  { value: "affiche", label: "Affiche" },
  { value: "etiquettes", label: "Etiquettes" },
  { value: "jeu", label: "Jeu" },
];

const NIVEAUX = [
  { value: "gs", label: "GS" },
  { value: "cp", label: "CP" },
  { value: "ce1", label: "CE1" },
  { value: "ce2", label: "CE2" },
  { value: "cm1", label: "CM1" },
  { value: "cm2", label: "CM2" },
];

const MATIERES = [
  { value: "lecture", label: "Lecture" },
  { value: "grammaire", label: "Grammaire" },
  { value: "dictee", label: "Dictee" },
  { value: "math", label: "Maths" },
  { value: "anglais", label: "Anglais" },
  { value: "sciences", label: "Sciences" },
  { value: "histoire", label: "Histoire" },
  { value: "geographie", label: "Geographie" },
  { value: "arts", label: "Arts" },
  { value: "eps", label: "EPS" },
  { value: "autre", label: "Autre" },
];

// ============================================================
// SKILL SELECTOR COMPONENT
// ============================================================

function SkillSelectorDialog({ open, onOpenChange, selectedSkills, onSkillsChange, matiere, niveau }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [repositories, setRepositories] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState("cycle2_programme_2025");

  useEffect(() => {
    if (open) {
      fetchRepositories();
      fetchSkills();
    }
  }, [open, matiere, niveau, selectedRepo, search]);

  const fetchRepositories = async () => {
    try {
      const { data } = await getSkillsRepositories();
      setRepositories(data);
    } catch (err) {
      console.error("Error fetching repositories:", err);
    }
  };

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const params = { repository_id: selectedRepo, limit: 50 };
      if (matiere) params.matiere = matiere;
      if (niveau) params.niveau = niveau;
      if (search) params.query = search;
      
      const { data } = await searchSkills(params);
      setSkills(data);
    } catch (err) {
      console.error("Error fetching skills:", err);
    } finally {
      setLoading(false);
    }
  };

  const isSelected = (skillId) => selectedSkills.some(s => s.skill_id === skillId);

  const toggleSkill = (skill) => {
    if (isSelected(skill.id)) {
      onSkillsChange(selectedSkills.filter(s => s.skill_id !== skill.id));
    } else {
      const isFirst = selectedSkills.length === 0;
      onSkillsChange([
        ...selectedSkills,
        { skill_id: skill.id, label: skill.label, is_main: isFirst },
      ]);
    }
  };

  const setMainSkill = (skillId) => {
    onSkillsChange(selectedSkills.map(s => ({
      ...s,
      is_main: s.skill_id === skillId,
    })));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            Selectionner des competences
          </DialogTitle>
          <DialogDescription>
            Liez cette ressource aux competences officielles du programme
          </DialogDescription>
        </DialogHeader>

        {/* Selected */}
        {selectedSkills.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
            {selectedSkills.map((skill) => (
              <Badge
                key={skill.skill_id}
                variant={skill.is_main ? "default" : "secondary"}
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => setMainSkill(skill.skill_id)}
              >
                {skill.is_main && <Star className="w-3 h-3" />}
                <span className="max-w-[180px] truncate">{skill.label}</span>
                <X
                  className="w-3 h-3 ml-1 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSkillsChange(selectedSkills.filter(s => s.skill_id !== skill.skill_id));
                  }}
                />
              </Badge>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <Select value={selectedRepo} onValueChange={setSelectedRepo}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Referentiel" />
            </SelectTrigger>
            <SelectContent>
              {repositories.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Skills list */}
        <ScrollArea className="flex-1 border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : skills.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-400">
              <GraduationCap className="w-8 h-8 mb-2" />
              <p>Aucune competence trouvee</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {skills.map((skill) => (
                <div
                  key={skill.id}
                  className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                    isSelected(skill.id)
                      ? "bg-indigo-50 border border-indigo-200"
                      : "hover:bg-slate-50"
                  }`}
                  onClick={() => toggleSkill(skill)}
                >
                  <Checkbox checked={isSelected(skill.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{skill.label}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge variant="outline" className="text-[10px]">{skill.category}</Badge>
                      {skill.niveaux?.slice(0, 3).map((n) => (
                        <Badge key={n} variant="outline" className="text-[10px]">{n.toUpperCase()}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// TARGET SELECTOR COMPONENT
// ============================================================

function TargetSelectorDialog({ open, onOpenChange, target, onTargetChange }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetType, setTargetType] = useState(target?.target_type || "whole_class");
  const [selectedIds, setSelectedIds] = useState(target?.student_ids || []);
  const [generateVariants, setGenerateVariants] = useState(target?.generate_variants || false);

  useEffect(() => {
    if (open) {
      fetchStudents();
      setTargetType(target?.target_type || "whole_class");
      setSelectedIds(target?.student_ids || []);
      setGenerateVariants(target?.generate_variants || false);
    }
  }, [open, target]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const { data } = await listStudents();
      setStudents(data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudent = (studentId) => {
    if (selectedIds.includes(studentId)) {
      setSelectedIds(selectedIds.filter(id => id !== studentId));
    } else {
      setSelectedIds([...selectedIds, studentId]);
    }
  };

  const handleSave = () => {
    onTargetChange({
      target_type: targetType,
      student_ids: targetType === "whole_class" ? [] : selectedIds,
      group_ids: [],
      group_name: null,
      generate_variants: generateVariants && targetType !== "whole_class" && selectedIds.length > 0,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Cible pedagogique
          </DialogTitle>
          <DialogDescription>
            Definissez a qui cette ressource est destinee
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Target type */}
          <div className="flex gap-2">
            <Button
              variant={targetType === "whole_class" ? "default" : "outline"}
              size="sm"
              onClick={() => setTargetType("whole_class")}
              className="flex-1"
            >
              <Users className="w-4 h-4 mr-1" />
              Classe entiere
            </Button>
            <Button
              variant={targetType === "individual" ? "default" : "outline"}
              size="sm"
              onClick={() => setTargetType("individual")}
              className="flex-1"
            >
              <Target className="w-4 h-4 mr-1" />
              Eleves specifiques
            </Button>
          </div>

          {/* Student selection */}
          {targetType !== "whole_class" && (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">{selectedIds.length} eleve(s)</span>
                <div className="flex gap-2">
                  <button 
                    className="text-indigo-600 hover:underline"
                    onClick={() => setSelectedIds(students.map(s => s.id))}
                  >
                    Tous
                  </button>
                  <button 
                    className="text-slate-500 hover:underline"
                    onClick={() => setSelectedIds([])}
                  >
                    Aucun
                  </button>
                </div>
              </div>

              <ScrollArea className="h-[200px] border rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {students.map((student) => (
                      <div
                        key={student.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                          selectedIds.includes(student.id)
                            ? "bg-indigo-50 border border-indigo-200"
                            : "hover:bg-slate-50"
                        }`}
                        onClick={() => toggleStudent(student.id)}
                      >
                        <Checkbox checked={selectedIds.includes(student.id)} />
                        <span className="text-sm">{student.student_name}</span>
                        <Badge variant="outline" className="ml-auto text-[10px]">
                          {student.niveau?.toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Generate variants option */}
              {selectedIds.length > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={generateVariants}
                      onCheckedChange={setGenerateVariants}
                    />
                    <div>
                      <p className="text-sm font-medium">Generer des variantes</p>
                      <p className="text-xs text-slate-500">
                        Creer automatiquement des versions differenciees
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSave}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// VARIANTS PANEL COMPONENT
// ============================================================

function VariantsPanel({ resourceId, variants, onRefresh }) {
  const [deleting, setDeleting] = useState(null);
  const [regenerating, setRegenerating] = useState(null);

  const handleDelete = async (variantId) => {
    setDeleting(variantId);
    try {
      await deleteResourceVariant(resourceId, variantId);
      toast.success("Variante supprimee");
      onRefresh();
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setDeleting(null);
    }
  };

  const handleRegenerate = async (variantId) => {
    setRegenerating(variantId);
    try {
      await regenerateVariant(variantId);
      toast.success("Variante regeneree");
      onRefresh();
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setRegenerating(null);
    }
  };

  if (!variants || variants.length === 0) {
    return (
      <div className="text-center py-6 text-slate-400">
        <Sparkles className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">Aucune variante generee</p>
        <p className="text-xs mt-1">Definissez une cible et sauvegardez pour generer</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{variants.length} variante(s)</span>
      </div>
      {variants.map((v) => (
        <div key={v.id} className="p-2 bg-slate-50 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{v.student_name || "Eleve"}</p>
              <p className="text-xs text-slate-400">
                {new Date(v.generated_at).toLocaleDateString("fr-FR")}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleRegenerate(v.id)}
                disabled={regenerating === v.id}
              >
                {regenerating === v.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                asChild
              >
                <a href={`/student/resource/${v.id}`} target="_blank" rel="noopener">
                  <ExternalLink className="w-3 h-3" />
                </a>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                onClick={() => handleDelete(v.id)}
                disabled={deleting === v.id}
              >
                {deleting === v.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT: RESOURCE METADATA SHEET
// ============================================================

export function ResourceMetadataSheet({
  open,
  onOpenChange,
  resourceId,
  documentTitle,
  elements,
  pages,
  pageSettings,
  onSaved,
  initialData = null,
}) {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Metadata state
  const [title, setTitle] = useState(documentTitle || "");
  const [niveau, setNiveau] = useState(initialData?.niveau || "");
  const [matiere, setMatiere] = useState(initialData?.matiere || "");
  const [resourceType, setResourceType] = useState(initialData?.resource_type || "fiche");
  const [status, setStatus] = useState(initialData?.status || "draft");
  const [tags, setTags] = useState(initialData?.tags || []);
  const [newTag, setNewTag] = useState("");
  
  // Skills
  const [skills, setSkills] = useState(initialData?.skills || []);
  const [skillsDialogOpen, setSkillsDialogOpen] = useState(false);
  
  // Target
  const [target, setTarget] = useState(initialData?.target || null);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  
  // Variants
  const [variants, setVariants] = useState([]);
  const [generatingVariants, setGeneratingVariants] = useState(false);

  useEffect(() => {
    if (open && resourceId) {
      fetchVariants();
    }
  }, [open, resourceId]);

  useEffect(() => {
    if (open) {
      setTitle(documentTitle || initialData?.title || "");
      setNiveau(initialData?.niveau || "");
      setMatiere(initialData?.matiere || "");
      setResourceType(initialData?.resource_type || "fiche");
      setStatus(initialData?.status || "draft");
      setTags(initialData?.tags || []);
      setSkills(initialData?.skills || []);
      setTarget(initialData?.target || null);
    }
  }, [open, documentTitle, initialData]);

  const fetchVariants = async () => {
    if (!resourceId) return;
    try {
      const { data } = await getResourceVariants(resourceId);
      setVariants(data.variants || []);
    } catch (err) {
      console.error("Error fetching variants:", err);
    }
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    if (!tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
    }
    setNewTag("");
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const mainSkill = skills.find(s => s.is_main);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const resourceData = {
        title: title.trim(),
        elements,
        pages,
        page_settings: pageSettings ? {
          format: pageSettings.format,
          orientation: pageSettings.orientation,
          width: pageSettings.width,
          height: pageSettings.height,
        } : null,
        niveau: niveau || null,
        matiere: matiere || null,
        resource_type: resourceType || null,
        status,
        tags,
        skills,
        target,
      };

      let result;
      if (resourceId) {
        const { data } = await updateResource(resourceId, resourceData);
        result = data;
        toast.success("Ressource mise a jour");
      } else {
        const { data } = await createResource(resourceData);
        result = data;
        toast.success("Ressource creee");
      }

      // Generate variants if requested
      if (target?.generate_variants && target?.student_ids?.length > 0 && result.id) {
        setGeneratingVariants(true);
        try {
          await generateResourceVariants(result.id, target.student_ids);
          toast.success("Variantes generees");
        } catch (err) {
          console.error("Error generating variants:", err);
          toast.error("Erreur lors de la generation des variantes");
        } finally {
          setGeneratingVariants(false);
        }
      }

      if (onSaved) onSaved(result);
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving:", err);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[450px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Proprietes de la ressource
          </SheetTitle>
          <SheetDescription>
            Configurez les metadonnees, competences et cibles
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="metadata" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="metadata" className="text-xs">
              <FileText className="w-3 h-3 mr-1" />
              General
            </TabsTrigger>
            <TabsTrigger value="skills" className="text-xs">
              <GraduationCap className="w-3 h-3 mr-1" />
              Competences
            </TabsTrigger>
            <TabsTrigger value="target" className="text-xs">
              <Target className="w-3 h-3 mr-1" />
              Cible
            </TabsTrigger>
          </TabsList>

          {/* METADATA TAB */}
          <TabsContent value="metadata" className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Titre *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre de la ressource"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Niveau</label>
                <Select value={niveau || "none"} onValueChange={(v) => setNiveau(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non defini</SelectItem>
                    {NIVEAUX.map((n) => (
                      <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Matiere</label>
                <Select value={matiere || "none"} onValueChange={(v) => setMatiere(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Matiere" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non defini</SelectItem>
                    {MATIERES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Type</label>
                <Select value={resourceType} onValueChange={setResourceType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOURCE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Statut</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="ready">Pret</SelectItem>
                    <SelectItem value="shared">Partage</SelectItem>
                    <SelectItem value="archived">Archive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="text-xs font-medium text-slate-500">Tags</label>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1 mb-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-red-500"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="Ajouter un tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                  className="h-8"
                />
                <Button size="sm" variant="outline" onClick={addTag} className="h-8">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* SKILLS TAB */}
          <TabsContent value="skills" className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Liez cette ressource aux competences officielles du programme pour le suivi pedagogique.
            </p>

            {skills.length > 0 ? (
              <div className="space-y-2">
                {skills.map((skill) => (
                  <div
                    key={skill.skill_id}
                    className={`p-2 rounded-lg border ${skill.is_main ? "bg-indigo-50 border-indigo-200" : "bg-slate-50"}`}
                  >
                    <div className="flex items-start gap-2">
                      {skill.is_main && <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                      <p className="text-sm flex-1">{skill.label}</p>
                      <X
                        className="w-4 h-4 text-slate-400 cursor-pointer hover:text-red-500"
                        onClick={() => setSkills(skills.filter(s => s.skill_id !== skill.skill_id))}
                      />
                    </div>
                    {skill.is_main && (
                      <Badge variant="outline" className="text-[10px] mt-1">Competence principale</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 border-2 border-dashed rounded-lg">
                <GraduationCap className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Aucune competence liee</p>
              </div>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => setSkillsDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {skills.length > 0 ? "Modifier les competences" : "Ajouter des competences"}
            </Button>
          </TabsContent>

          {/* TARGET TAB */}
          <TabsContent value="target" className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Definissez a qui cette ressource est destinee et generez des variantes differenciees.
            </p>

            {/* Current target display */}
            <div className="p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Cible actuelle</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTargetDialogOpen(true)}
                >
                  Modifier
                </Button>
              </div>
              {!target || target.target_type === "whole_class" ? (
                <div className="flex items-center gap-2 text-slate-600">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Classe entiere</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Target className="w-4 h-4" />
                    <span className="text-sm">
                      {target.student_ids?.length || 0} eleve(s) cible(s)
                    </span>
                  </div>
                  {target.generate_variants && (
                    <Badge variant="secondary" className="text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Variantes automatiques
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Variants section */}
            {resourceId && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Variantes generees</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchVariants}
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
                <VariantsPanel
                  resourceId={resourceId}
                  variants={variants}
                  onRefresh={fetchVariants}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Save button */}
        <div className="mt-6 pt-4 border-t">
          <Button
            className="w-full"
            onClick={handleSave}
            disabled={saving || generatingVariants}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : generatingVariants ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generation des variantes...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {resourceId ? "Mettre a jour" : "Creer la ressource"}
              </>
            )}
          </Button>
        </div>

        {/* Dialogs */}
        <SkillSelectorDialog
          open={skillsDialogOpen}
          onOpenChange={setSkillsDialogOpen}
          selectedSkills={skills}
          onSkillsChange={setSkills}
          matiere={matiere}
          niveau={niveau}
        />
        <TargetSelectorDialog
          open={targetDialogOpen}
          onOpenChange={setTargetDialogOpen}
          target={target}
          onTargetChange={setTarget}
        />
      </SheetContent>
    </Sheet>
  );
}
